import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEdgesState, useNodesState } from 'reactflow';
import { isDesignCanvasProject } from '../../../config/designMode';
import { useAuth } from '../../../contexts/Auth';
import useApi from '../../../hooks/useAPI';
import {
  evaluateConnection,
  getHandlePropertySchema,
  getMissingRequiredInputHandles,
} from '../../ProjectPackage/utils/validate';
import { injectCredentialEnums } from '../utils/injectCredentialEnums';
import { regionFromSourceNode } from '../utils/inheritRegion';
import { portTypeKey } from '../utils/portType';
import useEdgeOperations from './useEdgeOperations';
import useNodeOperations from './useNodeOperations';
import useProjectData from './useProjectData';
import useCanvasWebSocket from './useRobustWebSocket';

export const useFlowDiagram = () => {
  const { projectId } = useParams();
  const { token } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { projects: projectsApi, credentials: credentialsApi } = useApi();
  const { projectData, isLoading, error } = useProjectData(projectId);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapper = useRef(null);

  const updateEdges = useCallback(
    (updatedEdge) => {
      setEdges((eds) =>
        eds.map((edge) =>
          edge.id === updatedEdge.id ? { ...edge, ...updatedEdge } : edge
        )
      );
    },
    [setEdges]
  );

  const {
    sendJsonMessage,
    // connectionStatus,
    // error: wsError,
  } = useCanvasWebSocket(projectId, updateEdges);

  const {
    onOpenModal,
    onSubmitForm,
    onDeleteNode,
    onDeploy,
    onDestroy,
    onRunNow,
    onActivate,
    onDeactivate,
    updateNodeData,
    createNode,
  } = useNodeOperations(
    projectId,
    projectData,
    nodes,
    setNodes,
    edges,
    setEdges,
    reactFlowWrapper
  );

  const {
    onConnectStart,
    onConnectEnd,
    onConnectCheck,
    validateConnection,
    handleEdgeDelete,
  } = useEdgeOperations(projectId, projectData, nodes, edges, setEdges);

  const [modalState, setModalState] = useState({
    isModalOpen: false,
    isNameModalOpen: false,
    missingConnectionsModalOpen: false,
    selectedNodeId: null,
    formData: {},
    schema: {},
    droppedPackageInfo: null,
    missingConnections: [],
    deployPlanOpen: false,
    deployPlanLoading: false,
    deployPlan: null,
  });

  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance);
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const generateRandomSuffix = () => {
    return Math.random().toString(36).substring(2, 6).toUpperCase();
  };

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault();

      if (!reactFlowInstance) {
        console.warn('React Flow instance is not initialized');
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) {
        console.warn('React Flow wrapper bounds not available');
        return;
      }

      const packageData =
        event.dataTransfer.getData('application/reactflow') ||
        event.dataTransfer.getData('text/plain');

      if (!packageData) {
        console.error('No valid package data found in the drop event');
        return;
      }

      try {
        const packageInfo = JSON.parse(packageData);
        const position = reactFlowInstance.project({
          x: event.clientX - reactFlowBounds.left,
          y: event.clientY - reactFlowBounds.top,
        });

        const tempId = `temp-${Date.now()}`;
        const tempNode = {
          id: tempId,
          type: 'custom',
          position,
          data: {
            ...packageInfo,
            label: `${packageInfo.type.replace(
              /\//g,
              '-'
            )}-${generateRandomSuffix()}`,
            isTemp: true,
          },
        };

        // Add temporary node
        setNodes((nds) => nds.concat(tempNode));

        // Create the actual node
        try {
          const newNode = await createNode(
            {
              ...packageInfo,
              name: `${packageInfo.type.replace(
                /\//g,
                '-'
              )}-${generateRandomSuffix()}`,
            },
            position
          );

          // Replace temp node with actual node
          setNodes((nds) =>
            nds.map((node) =>
              node.id === tempId
                ? { ...newNode, position: node.position }
                : node
            )
          );
        } catch (error) {
          console.error('Error creating package:', error);
          enqueueSnackbar('Could not add block. Please try again.', { variant: 'error' });
          // Remove the temporary node if creation fails
          setNodes((nds) => nds.filter((node) => node.id !== tempId));
        }
      } catch (error) {
        console.error('Error parsing package data:', error);
      }
    },
    [reactFlowInstance, createNode, setNodes, enqueueSnackbar]
  );

  // const handleNameSubmit = useCallback(
  //   async (packageName) => {
  //     setModalState((prev) => ({ ...prev, isNameModalOpen: false }));
  //     if (!modalState.droppedPackageInfo) return;

  //     try {
  //       const { tempId, ...packageInfo } = modalState.droppedPackageInfo;
  //       const newNode = await createNode(
  //         {
  //           ...packageInfo,
  //           name: packageName,
  //         },
  //         packageInfo.position
  //       );

  //       setNodes((nds) =>
  //         nds.map((node) =>
  //           node.id === tempId ? { ...newNode, position: node.position } : node
  //         )
  //       );
  //       setTempNodes((temp) => temp.filter((node) => node.id !== tempId));
  //     } catch (error) {
  //       console.error('Error creating package:', error);
  //       // Remove the temporary node if creation fails
  //       setNodes((nds) =>
  //         nds.filter((node) => node.id !== modalState.droppedPackageInfo.tempId)
  //       );
  //       setTempNodes((temp) =>
  //         temp.filter(
  //           (node) => node.id !== modalState.droppedPackageInfo.tempId
  //         )
  //       );
  //     } finally {
  //       setModalState((prev) => ({ ...prev, droppedPackageInfo: null }));
  //     }
  //   },
  //   [modalState.droppedPackageInfo, createNode, setNodes]
  // );

  const checkRequiredConnections = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node?.data?.inputs?.properties) {
        return [];
      }
      const connectedInputs = edges
        .filter((edge) => edge.target === nodeId)
        .map((edge) => edge.targetHandle);

      return getMissingRequiredInputHandles(node.data.inputs, connectedInputs);
    },
    [nodes, edges]
  );

  const handleOpenModal = useCallback(
    async (nodeId) => {
      const modalData = onOpenModal(nodeId);
      if (!modalData) return;
      setModalState((prev) => ({
        ...prev,
        isModalOpen: true,
        selectedNodeId: modalData.selectedNodeId,
        formData: modalData.formData,
        schema: modalData.schema,
      }));
      if (isDesignCanvasProject(projectId)) return;
      try {
        const response = await credentialsApi.list();
        const creds = Array.isArray(response.body) ? response.body : [];
        const schema = injectCredentialEnums(modalData.schema, creds);
        setModalState((prev) => ({
          ...prev,
          schema,
        }));
      } catch (e) {
        console.error('Failed to load credentials for package form:', e);
      }
    },
    [onOpenModal, credentialsApi, projectId]
  );

  const handleSubmitForm = useCallback(
    async (nodeId, formData) => {
      if (!nodeId) {
        return;
      }
      // ModalsContainer passes (selectedNodeId, formData). Forward both to
      // useNodeOperations.onSubmitForm(nodeId, newFormData) — do not pass a
      // third argument or the UUID is mistaken for parameter_data.
      await onSubmitForm(nodeId, formData);
      setModalState((prev) => ({
        ...prev,
        isModalOpen: false,
        selectedNodeId: null,
        formData: {},
      }));
    },
    [onSubmitForm, setModalState]
  );

  const handleDeleteNode = useCallback(
    async (nodeId) => {
      await onDeleteNode(nodeId);
    },
    [onDeleteNode]
  );

  const handleDeleteEdge = useCallback(
    async (edgeId) => {
      console.log('handleDeleteEdge', edgeId);
      await handleEdgeDelete(edgeId);
    },
    [handleEdgeDelete]
  );

  const handleDeploy = useCallback(
    async (nodeId) => {
      const missingInputs = checkRequiredConnections(nodeId);
      if (missingInputs.length > 0) {
        setModalState((prev) => ({
          ...prev,
          missingConnectionsModalOpen: true,
          selectedNodeId: nodeId,
          missingConnections: missingInputs,
        }));
        return;
      }
      if (isDesignCanvasProject(projectId)) {
        await onDeploy(nodeId);
        return;
      }
      // Preview is the contract (dry-run): same resolver/order as the real
      // deploy, shown before anything commits. Cancel forgives.
      setModalState((prev) => ({
        ...prev,
        selectedNodeId: nodeId,
        deployPlanOpen: true,
        deployPlanLoading: true,
        deployPlan: null,
      }));
      try {
        const res = await projectsApi.previewDeployOrder(projectId, nodeId);
        setModalState((prev) => ({
          ...prev,
          deployPlanLoading: false,
          deployPlan: res?.body || null,
        }));
      } catch (e) {
        console.error('deploy-order preview failed:', e);
        setModalState((prev) => ({ ...prev, deployPlanOpen: false }));
        enqueueSnackbar('Deploy plan unavailable. Please retry when the connection is restored.', {
          variant: 'error',
        });
      }
    },
    [checkRequiredConnections, onDeploy, projectId, projectsApi, enqueueSnackbar]
  );

  const handleDestroy = useCallback(
    async (nodeId) => {
      await onDestroy(nodeId);
    },
    [onDestroy]
  );

  const handleRunNow = useCallback(
    async (nodeId) => {
      await onRunNow(nodeId);
    },
    [onRunNow]
  );

  const handleActivate = useCallback(
    async (nodeId) => {
      await onActivate(nodeId);
    },
    [onActivate]
  );

  const handleDeactivate = useCallback(
    async (nodeId) => {
      await onDeactivate(nodeId);
    },
    [onDeactivate]
  );

  const persistInheritedRegion = useCallback(
    async (targetNode, region) => {
      if (!targetNode?.data?.parameters?.properties?.region || !region) {
        return;
      }
      const parameterData = {
        ...(targetNode.data.parameter_data || {}),
        region,
      };
      setNodes((nds) =>
        nds.map((node) =>
          node.id === targetNode.id
            ? { ...node, data: { ...node.data, parameter_data: parameterData } }
            : node
        )
      );
      if (isDesignCanvasProject(projectId)) return;
      try {
        await projectsApi.updatePackage(
          projectId,
          targetNode.id,
          { parameter_data: parameterData },
          token
        );
      } catch (error) {
        console.error('Failed to persist inherited region:', error);
      }
    },
    [projectId, projectsApi, setNodes, token]
  );

  const onConnect = useCallback(
    async (params) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      const result = evaluateConnection({
        sourceNode,
        targetNode,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
      });
      if (!result.valid) {
        enqueueSnackbar(result.reason || 'Invalid connection', {
          variant: 'warning',
        });
        return;
      }

      const sourceSchema = getHandlePropertySchema(
        sourceNode?.data?.outputs,
        params.sourceHandle
      );
      const pendingId = `${params.source}-${params.target}-${params.sourceHandle}-${params.targetHandle}`;
      const newEdge = {
        ...params,
        id: pendingId,
        type: 'custom',
        animated: true,
      };
      setEdges((eds) => [...eds, newEdge]);

      try {
        if (isDesignCanvasProject(projectId)) {
          const edgeId = `design-conn-${crypto.randomUUID()}`;
          const updatedEdge = {
            ...newEdge,
            id: edgeId,
            type: 'custom',
            animated: false,
            data: {
              connectionType: portTypeKey(sourceSchema),
              targetDeployStatus:
                targetNode?.data?.deploy_status || 'NOT_DEPLOYED',
              onDelete: handleEdgeDelete,
            },
          };
          setEdges((eds) =>
            eds.map((e) => (e.id === pendingId ? updatedEdge : e))
          );
          await persistInheritedRegion(
            targetNode,
            regionFromSourceNode(sourceNode, params.sourceHandle)
          );
          return;
        }
        const response = await projectsApi.createConnection(
          projectId,
          {
            source_package_id: params.source,
            target_package_id: params.target,
            source_handle: params.sourceHandle,
            target_handle: params.targetHandle,
          },
          token
        );

        if (response && response.body) {
          const updatedEdge = {
            ...newEdge,
            ...response.body,
            type: 'custom',
            animated: false,
            data: {
              ...(response.body.data || {}),
              connectionType: portTypeKey(sourceSchema),
              targetDeployStatus:
                targetNode?.data?.deploy_status || 'NOT_DEPLOYED',
              onDelete: handleEdgeDelete,
            },
          };
          setEdges((eds) =>
            eds.map((e) => (e.id === pendingId ? updatedEdge : e))
          );
          await persistInheritedRegion(
            targetNode,
            regionFromSourceNode(sourceNode, params.sourceHandle)
          );
        } else {
          setEdges((eds) => eds.filter((e) => e.id !== pendingId));
        }
      } catch (error) {
        console.error('Error saving connection:', error);
        setEdges((eds) => eds.filter((e) => e.id !== pendingId));
        enqueueSnackbar('Failed to save connection', { variant: 'error' });
      }
    },
    [
      projectId,
      token,
      projectsApi,
      setEdges,
      nodes,
      handleEdgeDelete,
      enqueueSnackbar,
      persistInheritedRegion,
    ]
  );

  const nodesWithFunctions = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      deletable: false,
      data: {
        ...node.data,
        updateNodeData: (newData) => updateNodeData(node.id, newData),
        onOpenModal: () => handleOpenModal(node.id),
        onDeploy: () => handleDeploy(node.id),
        onDestroy: () => handleDestroy(node.id),
        onRunNow: () => handleRunNow(node.id),
        onActivate: () => handleActivate(node.id),
        onDeactivate: () => handleDeactivate(node.id),
        onRun: isDesignCanvasProject(projectId)
          ? () => handleDeploy(node.id)
          : undefined,
        onDelete: () => handleDeleteNode(node.id),
        onDeleteEdge: (edgeId) => handleDeleteEdge(edgeId),
        deploy_status: node.data?.deploy_status || 'NOT_DEPLOYED',
      },
    }));
  }, [
    nodes,
    projectId,
    handleOpenModal,
    handleDeploy,
    handleDestroy,
    handleRunNow,
    handleActivate,
    handleDeactivate,
    handleDeleteNode,
    updateNodeData,
    handleDeleteEdge,
  ]);

  const edgesWithDelete = useMemo(() => {
    return edges.map((edge) => {
      const target = nodes.find((n) => n.id === edge.target);
      const targetDeployStatus =
        target?.data?.deploy_status || 'NOT_DEPLOYED';
      return {
        ...edge,
        deletable: targetDeployStatus === 'NOT_DEPLOYED',
        data: {
          ...(edge.data || {}),
          targetDeployStatus,
          onDelete: handleEdgeDelete,
        },
      };
    });
  }, [edges, nodes, handleEdgeDelete]);

  const onEdgesDelete = useCallback(
    (deleted) => {
      (deleted || []).forEach((edge) => {
        if (edge?.id) handleEdgeDelete(edge.id);
      });
    },
    [handleEdgeDelete]
  );

  return {
    nodes: nodesWithFunctions,
    edges: edgesWithDelete,
    onNodesChange,
    onEdgesChange,
    onEdgesDelete,
    onConnect,
    onConnectStart,
    onConnectEnd,
    onConnectCheck,
    onInit,
    onDrop,
    onDragOver,
    isLoading,
    error,
    reactFlowWrapper,
    // handleNameSubmit,
    modalState,
    setModalState,
    handleOpenModal,
    handleEdgeDelete,
    onSubmitForm,
    handleSubmitForm,
    handleDeleteNode,
    handleDeploy,
    handleDestroy,
    /** Raw deploy (no validation/preview) for explicit user confirmation paths. */
    deployRaw: onDeploy,
    setNodes,
    updateNodeData,
    checkRequiredConnections,
    validateConnection,
  };
};

export default useFlowDiagram;
